import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { rmsApi } from '../../../state/axios';
import type { RootState } from '../../../state/store';
import { buildExportTimeRangeParam } from '../helpers/buildExportTimeRange';
import { EXPORT_COLORS } from '../constants';
import type { ExportDateRange, ReportType } from '../types';

// Hook for handling export operations and error management
export const useExportHandlers = () => {
  const navigate = useNavigate();
  const routeMatch = useParams();
  const companyDetails = useSelector((state: RootState) => state.company) as any;
  const { filters } = useSelector((state: RootState) => state);
  const { dates, timeRange, locations: locationFilters } = filters;

  const initiateReportExport = async (
    reportVariation: ReportType,
    exportDateRange?: ExportDateRange
  ) => {
    try {
      // Build the export URL
      const baseUrl = `/api/v1/dar/${routeMatch.dashboard}/${routeMatch.widget}/report/download/${reportVariation.url}`;

      // Prepare request data
      const requestData: any = {};

      const dateRangeToUse: ExportDateRange = exportDateRange || {
        dates: dates as [string, string],
        timeRange: timeRange as [[number, number], [number, number]]
      };

      requestData.time_range = buildExportTimeRangeParam(
        dateRangeToUse,
        companyDetails?.company_details?.time_zone_offset || '+00:00'
      );

      if (companyDetails?.company_details?.company_id) {
        requestData.company_id = companyDetails.company_details.company_id.toString();
      }

      if (locationFilters && locationFilters.length > 0) {
        requestData.location_ids = locationFilters.join(',');
      }

      if (companyDetails?.company_details?.currency_code) {
        requestData.currency_code = companyDetails.company_details.currency_code;
      }

      // Step 1: Initiate the export using POST
      const response = await rmsApi.post(baseUrl, requestData);

      if (response.status === 200) {
        return { success: true, message: 'Export initiated successfully' };
      } else {
        throw new Error(`Export initiation failed: ${response.status}`);
      }
    } catch (error) {
      console.error('Export error:', error);
      throw error;
    }
  };

  const handleExportSuccess = () => {
    Swal.fire({
      title: 'Export Initiated',
      text: 'Status updates will be available by email and in Saved Reports.',
      icon: 'success',
      timer: 3000,
      showConfirmButton: false
    });
  };

  const handleExportError = (error: any, reportType: ReportType, onRetry: () => void) => {
    console.error('Export error:', error);

    if (
      error.response?.data?.error_code === 'already_downloaded' ||
      error.response?.data?.error_code === 'duplicate_export_detected'
    ) {
      const downloadUrl = error.response?.data?.download_url;
      const hasDownloadUrl = downloadUrl && downloadUrl.trim() !== '';

      Swal.fire({
        title: 'Report Already Available',
        html: `
          <div style="text-align: center; padding: 10px 0;">
            <p style="margin-bottom: 16px; color: #666;">
              <strong style="color: ${EXPORT_COLORS.text};">${reportType.name}</strong> has already been generated recently.
            </p>
            ${
              hasDownloadUrl
                ? `
              <div style="background: #e8f5e8; padding: 16px; border-radius: 8px; margin: 16px 0; border: 1px solid #4caf50;">
                <p style="margin: 0 0 12px 0; color: #2e7d32; font-size: 14px; font-weight: 500;">
                  📥 Download Available
                </p>
                <button id="direct-download-btn" class="btn btn-success" style="padding: 8px 16px; border-radius: 6px; border: none; background: #4caf50; color: white; cursor: pointer;">
                  Download Now
                </button>
              </div>
            `
                : `
              <div style="background: ${EXPORT_COLORS.infoBg}; padding: 16px; border-radius: 8px; margin: 16px 0;">
                <p style="margin: 0; color: ${EXPORT_COLORS.infoText}; font-size: 14px;">
                  You can find it in your Export History
                </p>
              </div>
            `
            }
          </div>
        `,
        icon: 'info',
        showConfirmButton: !hasDownloadUrl,
        confirmButtonText: hasDownloadUrl ? 'View Export History' : 'View Export History',
        showCancelButton: true,
        cancelButtonText: hasDownloadUrl ? 'View Export History' : 'OK',
        confirmButtonColor: EXPORT_COLORS.primary,
        cancelButtonColor: EXPORT_COLORS.secondary,
        customClass: {
          popup: 'export-existing-popup'
        },
        didOpen: () => {
          if (hasDownloadUrl) {
            const downloadBtn = document.getElementById('direct-download-btn');
            if (downloadBtn) {
              downloadBtn.addEventListener('click', () => {
                // Create a temporary anchor element for direct download
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = '';
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                Swal.close();
              });
            }
          }
        }
      }).then((result) => {
        if (result.isConfirmed && !hasDownloadUrl) {
          handleViewSavedReportsClick();
        }
      });
    } else if (
      error.response?.status === 423 &&
      (error.response?.data?.error_code === 'concurrent_export_detected' ||
        error.response?.data?.error_code === 'export_in_progress')
    ) {
      // Handle concurrent export validation error
      const errorData = error.response.data;

      Swal.fire({
        title: 'Export Already in Progress',
        html: `
          <div style="text-align: center; padding: 10px 0;">
            <div style="background: ${
              EXPORT_COLORS.infoBg
            }; padding: 16px; border-radius: 8px; margin: 16px 0;">
              <p style="margin: 0; color: ${
                EXPORT_COLORS.infoText
              }; font-size: 14px; line-height: 1.5;">
                ${
                  errorData.message ||
                  'An export is already in progress for the selected time range.'
                }
              </p>
            </div>
            <p style="margin: 0; color: #666; font-size: 13px;">
              Please wait for the current export to complete, then try again.
            </p>
          </div>
        `,
        icon: 'info',
        confirmButtonText: 'View Export History',
        showCancelButton: true,
        cancelButtonText: 'OK',
        confirmButtonColor: EXPORT_COLORS.primary,
        cancelButtonColor: EXPORT_COLORS.secondary
      }).then((result) => {
        if (result.isConfirmed) {
          handleViewSavedReportsClick();
        }
      });
    } else {
      // Extract the proper error message from the response
      let errorMessage = 'Unknown error occurred';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      const limitExceededError = errorMessage?.toLowerCase().includes('upgrade to pro');

      Swal.fire({
        title: limitExceededError ? 'Export Limit Exceeded' : 'Export Failed',
        html: `
          <div style="text-align: center; padding: 10px 0;">
            <div style="background: ${
              EXPORT_COLORS.errorLightBg
            }; padding: 16px; border-radius: 8px; margin: 16px 0;">
              <p style="margin: 0; color: ${
                EXPORT_COLORS.error
              }; font-size: 13px; line-height: 1.4;">
                Error: ${errorMessage}
              </p>
            </div>
          </div>
        `,
        icon: 'error',
        showCancelButton: true,
        showConfirmButton: limitExceededError,
        confirmButtonText: limitExceededError ? 'Upgrade to Pro' : undefined,
        confirmButtonColor: limitExceededError ? EXPORT_COLORS.primary : undefined,
        cancelButtonText: 'OK',
        cancelButtonColor: EXPORT_COLORS.secondary
      }).then((result) => {
        if (result.isConfirmed && limitExceededError) {
          window.open('https://www.sapaad.com/campaign/vantage-pro/', '_blank');
        } else if (result.isConfirmed) {
          onRetry();
        }
      });
    }
  };

  const handleQuotaCheckError = () => {
    Swal.fire({
      title: 'Quota Check Failed',
      text: 'Failed to check export quota. Please try again.',
      icon: 'error',
      confirmButtonText: 'OK',
      confirmButtonColor: EXPORT_COLORS.error
    });
  };

  const handleViewSavedReportsClick = () => {
    navigate('/saved-reports');
  };

  return {
    initiateReportExport,
    handleExportSuccess,
    handleExportError,
    handleQuotaCheckError,
    handleViewSavedReportsClick
  };
};
